import type {
    BusinessTripFormPayload,
    BusinessTripFormResponse,
} from '@/types/apps/business-trip-form.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';
import { dateUtils } from '@/utils/date';

type BusinessTripFormListResponse = ApiResponse<
    PaginatedResult<BusinessTripFormResponse>
>;

export class BusinessTripFormService
    implements Showable, Updatable, Deletable, Creatable, Indexable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        base: () => '/api/BusinessTripForms',
        detail: (id: string) => `/api/BusinessTripForms/${id}`,
        createWithDetail: () => '/api/BusinessTripForms/with-detail',
        restore: () => '/api/BusinessTripForms/restore',
    };

    public async index(
        params?: QueryParams
    ): Promise<BusinessTripFormListResponse> {
        const defaultParams: QueryParams = {
            SortBy: 'CreatedDate DESC',
        };

        const finalParams = {
            ...defaultParams,
            ...params,
        };

        return this.apiClient
            .fetchApi<
                PaginatedResult<BusinessTripFormResponse>
            >(this.endpoints.base(), finalParams)
            .then(res => {
                return {
                    ...res,
                    result: {
                        ...res.result,
                        items: res.result.items.map(item =>
                            this.formatResponse(item)
                        ),
                    },
                };
            });
    }

    public async detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<BusinessTripFormResponse>> {
        return this.apiClient
            .fetchApi(this.endpoints.detail(id), params)
            .then(res => {
                return {
                    ...res,
                    result: this.formatResponse(res.result as any),
                };
            });
    }

    private formatResponse(
        data: BusinessTripFormResponse
    ): BusinessTripFormResponse {
        return {
            ...data,
            customName: `${data.code} (${dateUtils.getDateTimeLocal(data.checkinPlan)} - ${dateUtils.getDateTimeLocal(data.checkoutPlan)})`,
        };
    }

    public create(
        payload: BusinessTripFormPayload
    ): Promise<ApiResponse<BusinessTripFormResponse>> {
        return this.apiClient.postApi(this.endpoints.base(), payload);
    }

    public createWithDetail(
        payload: BusinessTripFormPayload
    ): Promise<ApiResponse<BusinessTripFormResponse>> {
        return this.apiClient.postApi(
            this.endpoints.createWithDetail(),
            payload
        );
    }

    public update(
        id: string,
        payload: BusinessTripFormPayload
    ): Promise<ApiResponse<BusinessTripFormResponse>> {
        return this.apiClient.putApi(this.endpoints.detail(id), payload);
    }

    public restore(
        ids: string[]
    ): Promise<ApiResponse<BusinessTripFormResponse[]>> {
        return this.apiClient.putApi(this.endpoints.restore(), ids);
    }

    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        return this.apiClient.destroyApi(
            this.endpoints.base(),
            ids,
            isHardDeleted
        );
    }
}
